import {
  useContext,
  useEffect,
  useId,
  useRef,
  type KeyboardEvent,
} from 'react';
// eslint-disable-next-line no-restricted-imports -- Reuse the control used by docs ContentSwitcher.
import SelectGroup from 'plaid-threads/SelectGroup';
// eslint-disable-next-line no-restricted-imports -- Style SelectGroup's selection indicator through its shared Button styles.
import { styles as buttonStyles } from 'plaid-threads/Button';
import { styled } from 'threads5/styles/styled';

import DocsContext from 'src/contexts/docs';
import type { DocsState } from 'src/contexts/docs/reducer';

const options: Array<{ label: string; value: DocsState['goSdkGeneration'] }> = [
  { label: 'Legacy SDK', value: 'legacy' },
  { label: 'New SDK', value: 'fern' },
];

const StyledToggle = styled('div')(({ theme }) => {
  return {
    '--color-fill-inverse': 'currentColor',
    '--color-text-high-contrast': 'currentColor',
    '--color-text-state-hover': 'currentColor',
    '--color-text-state-pressed': 'currentColor',
    color: '#24292e',
    '& [role="radiogroup"]': {
      display: 'inline-flex',
      flexWrap: 'nowrap',
      gap: theme.spacing(0.5),
      padding: 3,
      border: '1px solid #a8b5bb',
      borderRadius: 8,
    },
    '& [role="radio"]': {
      margin: 0,
      height: 'auto',
      padding: '5px 10px',
      border: 0,
      borderRadius: 5,
      fontSize: '1.2rem',
      lineHeight: '1.6rem',
      whiteSpace: 'nowrap',
      color: 'inherit',
      background: 'transparent',
      '&:hover': { border: 0, background: '#e6edf7' },
      '&[aria-checked="true"]': { background: '#2563eb', color: '#fff' },
      '&:focus-visible': {
        outline: '2px solid #2563eb',
        outlineOffset: 3,
      },
      [`& .${buttonStyles.leadingIcon}`]: { display: 'none' },
    },
    '&[data-theme="dark"]': {
      color: '#e6edf7',
      '& [role="radiogroup"]': { borderColor: '#6c7592' },
      '& [role="radio"][aria-checked="false"]:hover': {
        background: '#243b64',
      },
    },
  };
});

export const GoSdkToggle = ({ dark }: { dark: boolean }) => {
  const { goSdkGeneration, dispatch } = useContext(DocsContext);
  const labelId = useId();
  const containerRef = useRef<HTMLDivElement>(null);

  // SelectGroup supplies radio semantics but not roving focus or arrow keys.
  useEffect(() => {
    containerRef.current
      ?.querySelectorAll<HTMLButtonElement>('[role="radio"]')
      .forEach((radio) => {
        radio.tabIndex = radio.getAttribute('aria-checked') === 'true' ? 0 : -1;
      });
  }, [goSdkGeneration]);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const direction = {
      ArrowRight: 1,
      ArrowDown: 1,
      ArrowLeft: -1,
      ArrowUp: -1,
    }[event.key];
    if (!direction) return;
    const radios = Array.from(
      event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="radio"]'),
    );
    const index = radios.indexOf(event.target as HTMLButtonElement);
    if (index === -1) return;
    event.preventDefault();
    const next = radios[(index + direction + radios.length) % radios.length];
    next.focus();
    next.click();
  };

  return (
    <StyledToggle
      ref={containerRef}
      data-theme={dark ? 'dark' : 'light'}
      onKeyDown={handleKeyDown}
    >
      <span id={labelId} hidden>
        Go SDK generation
      </span>
      <SelectGroup
        labeledById={labelId}
        options={options}
        value={options.find(({ value }) => {
          return value === goSdkGeneration;
        })}
        onChange={({ value }) => {
          return dispatch({ type: 'SET_GO_SDK_GENERATION', payload: value });
        }}
        small
        inline
      />
    </StyledToggle>
  );
};
